import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [filters] - Category filter buttons
 * @property {HTMLElement[]} [items] - Grid items (one per project)
 * @property {HTMLElement} [status] - Live region announcing the number of projects shown
 * @property {HTMLDialogElement} [dialog] - Lightbox
 * @property {HTMLElement[]} [slides] - Lightbox slides, same order as the items
 * @property {HTMLElement} [counter] - "3 / 10" in the lightbox
 * @property {HTMLButtonElement} [closeButton] - Lightbox close button
 */

/**
 * Projects gallery: category filters (instant, with counts) and a lightbox (native <dialog>)
 * showing one large before / after slider at a time. Arrows, Arrow keys and horizontal swipe
 * (outside the slider) go through the projects of the active filter; the close button, Escape
 * and a click on the dark background close it; page scroll is locked while it is open and
 * focus returns to the card that opened it.
 *
 * @extends {Component<Refs>}
 */
class AbsolutGallery extends Component {
  /** @type {number} Index of the slide shown in the lightbox */
  #current = 0;

  /** @type {HTMLElement | null} Card that opened the lightbox */
  #opener = null;

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {{ x: number, y: number } | null} */
  #swipeStart = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Listeners on the component (capture: "close" does not bubble), filtered to the lightbox,
    // so they do not depend on refs being ready and survive section re-renders
    this.addEventListener('close', this.#handleClose, { signal, capture: true });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#unlockScroll();
  }

  /**
   * Shows only the projects of the clicked category ("all" shows every project).
   * @param {Event} event - Click on a filter button
   */
  filter(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-category]');
    if (!(button instanceof HTMLElement)) return;

    const category = button.dataset.category ?? 'all';
    for (const filter of this.refs.filters ?? []) {
      filter.setAttribute('aria-pressed', String(filter === button));
    }

    let shown = 0;
    for (const item of this.refs.items ?? []) {
      const visible = category === 'all' || item.dataset.category === category;
      item.hidden = !visible;
      if (visible) shown += 1;
    }

    this.#announce(shown);
  }

  /**
   * Opens the lightbox on the clicked card.
   * @param {Event} event - Click on a card
   */
  open(event) {
    if (!(event.target instanceof Element)) return;

    const card = event.target.closest('[data-index]');
    const { dialog } = this.refs;
    if (!(card instanceof HTMLElement) || !dialog) return;

    this.#opener = card;
    this.#show(Number(card.dataset.index));
    this.#lockScroll();
    dialog.showModal();
    this.refs.closeButton?.focus();
  }

  close() {
    this.refs.dialog?.close();
    this.#restore();
  }

  previous() {
    this.#step(-1);
  }

  next() {
    this.#step(1);
  }

  /**
   * Closes the lightbox on a click on the dark background (not on its content).
   * @param {MouseEvent} event - Click inside the dialog
   */
  backdrop(event) {
    const target = event.target;
    if (target === this.refs.dialog || (target instanceof HTMLElement && target.hasAttribute('data-backdrop'))) {
      this.close();
    }
  }

  /** Indexes of the projects left visible by the active filter, in grid order */
  get #visibleIndexes() {
    return (this.refs.items ?? []).flatMap((item, index) => (item.hidden ? [] : [index]));
  }

  /**
   * Moves to the previous / next project of the active filter (wraps around).
   * @param {number} direction - -1 or 1
   */
  #step(direction) {
    const indexes = this.#visibleIndexes;
    if (indexes.length < 2) return;

    const position = Math.max(0, indexes.indexOf(this.#current));
    const next = indexes[(position + direction + indexes.length) % indexes.length];
    if (next !== undefined) this.#show(next);
  }

  /**
   * Shows one slide and updates the counter and the dialog name.
   * @param {number} index - Slide index
   */
  #show(index) {
    const slides = this.refs.slides ?? [];
    const { dialog, counter } = this.refs;
    this.#current = index;

    slides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== index;
    });

    const title = slides[index]?.querySelector('[id^="AbsolutGallerySlideTitle-"]');
    if (dialog && title) {
      dialog.removeAttribute('aria-label');
      dialog.setAttribute('aria-labelledby', title.id);
    }

    const indexes = this.#visibleIndexes;
    const position = indexes.indexOf(index) + 1;
    if (counter) {
      counter.textContent = (this.dataset.counter ?? '[current] / [total]')
        .replace('[current]', String(position))
        .replace('[total]', String(indexes.length));
    }

    const single = indexes.length < 2;
    for (const button of this.querySelectorAll('.ab-gallery__arrow')) {
      if (button instanceof HTMLElement) button.hidden = single;
    }
  }

  /** @param {number} count - Number of projects shown */
  #announce(count) {
    const { status } = this.refs;
    if (!status) return;

    const template = count === 1 ? this.dataset.countOne : this.dataset.countOther;
    status.textContent = (template ?? '[count]').replace('[count]', String(count));
  }

  /**
   * Native close (Escape): restores scroll and focus.
   * @param {Event} event
   */
  #handleClose = (event) => {
    if (event.target !== this.refs.dialog) return;
    this.#restore();
  };

  /** Unlocks the page scroll and puts focus back on the card that opened the lightbox (once) */
  #restore() {
    if (!this.#opener) return;
    const opener = this.#opener;
    this.#opener = null;
    this.#unlockScroll();
    opener.focus();
  }

  /**
   * @param {Event} event
   * @returns {boolean} Whether the event comes from the open lightbox
   */
  #inOpenDialog(event) {
    const { dialog } = this.refs;
    return Boolean(dialog?.open && event.target instanceof Node && dialog.contains(event.target));
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (!this.#inOpenDialog(event)) return;

    // Arrow keys on the slider move the slider, not the gallery
    if (event.target instanceof HTMLInputElement && event.target.type === 'range') return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(rtl ? -1 : 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(rtl ? 1 : -1);
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (!this.#inOpenDialog(event)) return;
    // Swipes that start on the comparison slider drag the slider instead
    const onSlider = event.target instanceof Element && event.target.closest('.ab-compare__media');
    this.#swipeStart = event.pointerType === 'mouse' || onSlider ? null : { x: event.clientX, y: event.clientY };
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    const start = this.#swipeStart;
    this.#swipeStart = null;
    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    this.#step((dx < 0) !== rtl ? 1 : -1);
  };

  #lockScroll() {
    document.documentElement.style.setProperty('overflow', 'hidden');
  }

  #unlockScroll() {
    document.documentElement.style.removeProperty('overflow');
  }
}

if (!customElements.get('absolut-gallery')) {
  customElements.define('absolut-gallery', AbsolutGallery);
}
